import React, {
  ChangeEventHandler,
  FocusEventHandler,
  useRef,
  useState,
} from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { IRootState } from '../../redux/reducers'
import { UserPermissionsState } from '../../redux/reducers/initialState'
import * as constants from '../../utils'
import ExpandableRow from '../common/ExpandableRow'
import GenericModal from '../common/GenericModal'
import TOverlayTrigger from '../common/OverlayTrigger'

import KeyUpload from './KeyUpload'
import TBody from './TBody'
import TDeleteLink from './TDeleteLink'
import TEditLink from './TEditLink'
import TFooter from './TFooter'
import TRowEmpty from './TRowEmpty'
import { KeyInfo } from './types'
import { isScreenSizeSmall } from './utils'

import '../../css/common/KeyList.css'

export type KeyListProps = {
  keys: any[]
  onLoadMoreClick: () => void
  onDeleteClick: (endorsementKey: KeyInfo) => void
  onSearchClick: () => void
  loading: boolean
  handleChange: ChangeEventHandler<HTMLInputElement>
  handleBlur: FocusEventHandler<HTMLInputElement>
  keysExists: boolean
  request: any
  pageNum: number
  disableLoadMore: boolean
  updatePageNum: (pageNum: number) => void
  editKeyPath: string
  showUploadButton: boolean
  uploadKey: any
}

const KeyList = ({
  keys,
  onLoadMoreClick,
  onDeleteClick,
  onSearchClick,
  loading,
  handleChange,
  handleBlur,
  request,
  keysExists,
  pageNum,
  disableLoadMore,
  updatePageNum,
  editKeyPath,
  showUploadButton,
  uploadKey,
}: KeyListProps) => {
  const initForm = {
    keyName: '',
    aadRevocation: '',
  }
  const { t } = useTranslation()
  const keysPerPage = constants.recordsPerPage
  const totalPages = Math.ceil(keys.length / keysPerPage)
  const showingText = `Showing ${
    (pageNum - 1) * keysPerPage + 1
  }  to  ${Math.min(keys.length, pageNum * keysPerPage)}  of ${keys.length}`

  const [formData, setFormData] = useState(initForm)
  const [uploadData, setUploadData] = useState({})
  const [formReady, setFormReady] = useState(false)
  const refFormData: any = useRef()
  const refUploadData: any = useRef()
  const refValidateData: any = useRef()
  refFormData.current = formData
  refUploadData.current = uploadData

  const initFormData = () => {
    setFormData(initForm)
    setUploadData({})
  }

  const userPermissions: UserPermissionsState | null = useSelector(
    (state: IRootState) => state.userPermissions,
  )
  const isAuthorized: boolean = useSelector(
    (state: IRootState) => state.identity != null,
  )

  const disableGetKeys =
    loading ||
    !(
      isAuthorized &&
      userPermissions !== null &&
      userPermissions.hasGetKeysAccess
    )
  const disableEditKeys = !(
    isAuthorized &&
    userPermissions !== null &&
    userPermissions.hasUpdateKeysAccess
  )
  const disableDeleteKeys = !(
    isAuthorized &&
    userPermissions !== null &&
    userPermissions.hasDeleteKeysAccess
  )
  const disableUploadKeys =
    loading ||
    !(
      isAuthorized &&
      userPermissions !== null &&
      userPermissions.hasUploadKeysAccess
    )

  const handlePageChange = (event, page) => updatePageNum(page)

  const validateSaveUploadKeyModal = () => {
    const validKeyName = { ...formData, keyName: formData.keyName.trim() }
    setFormData(validKeyName)
    refValidateData.current.validate()
    return formReady
  }
  const handleSaveUploadKeyModal = async () => {
    const file = refUploadData.current
    const keyname = refFormData.current['keyName']
    const aadRevocation = refFormData.current['aadRevocation']

    await uploadKey(file, keyname, aadRevocation)
    initFormData()
  }

  const handleCloseUploadKeyModal = () => {
    initFormData()
  }

  const handleEnterKeyPress = (event) => {
    if (event.key == 'Enter') {
      return onSearchClick()
    }
  }

  const displayKeyRow = (key) => {
    const lastAccessedDate = new Date(key.lastAccessed)
    const importDate = new Date(key.importDate * 1000) // Create date from millisec import date timestamp

    return (
      <ExpandableRow rowIndex={key.keyId}>
        {(isExpanded, toggleExpansion) => (
          <tr
            key={key.keyId}
            className={isExpanded() ? 'expandedRow' : 'tableRow'}
          >
            <td className="tableCell" onClick={toggleExpansion}>
              {key.keyName}
            </td>
            <td className="tableCell" onClick={toggleExpansion}>
              {key.keyId}
            </td>
            <td className="tableCell" onClick={toggleExpansion}>
              {key.aadRevocation}
            </td>
            <TOverlayTrigger
              tooltipId="tooltip-lastAccessed"
              tooltipVal={
                key.lastAccessed == null || key.lastAccessed == ''
                  ? null
                  : lastAccessedDate.toLocaleString()
              }
              tooltipChild={
                <td onClick={toggleExpansion}>
                  {key.lastAccessed == null
                    ? ''
                    : lastAccessedDate.toLocaleDateString()}
                </td>
              }
            />
            <TOverlayTrigger
              tooltipId="tooltip-importedOn"
              tooltipVal={importDate.toLocaleString()}
              tooltipChild={
                <td onClick={toggleExpansion}>
                  {importDate.toLocaleDateString()}
                </td>
              }
            />

            <td>
              <TEditLink
                disable={disableEditKeys}
                pathName={editKeyPath + key.rowKey}
              />
            </td>
            <td>
              <TDeleteLink
                disable={disableDeleteKeys}
                title={t('key.delete')}
                onDelete={() => onDeleteClick(key)}
                itemName={key.keyName}
                showNoteSoftDelete={true}
              />
            </td>
          </tr>
        )}
      </ExpandableRow>
    )
  }

  const TRow = () =>
    keysExists ? (
      <>
        {keys
          .slice((pageNum - 1) * keysPerPage, pageNum * keysPerPage)
          .map((key) => {
            return displayKeyRow(key)
          })}
      </>
    ) : (
      <TRowEmpty colSpan="5" emptyRowText={t('key.no_keys_found')} />
    )

  return (
    <>
      <div className="scrollable responsive-font">
        <Container fluid>
          <Row className="g-3">
            <Col xs={12} lg={3} className="mt-4">
              <input
                name="keyName"
                type="text"
                placeholder={t('key.name')}
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleEnterKeyPress}
                value={request.keyName}
                disabled={request.keyId.length > 0}
              />
            </Col>
            <Col xs={12} lg={3} className="mt-4">
              <input
                name="keyId"
                type="text"
                placeholder={t('key.key_id')}
                className="form-control responsive-font"
                disabled={
                  request.keyName.length > 0 || request.aadRevocation.length > 0
                }
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleEnterKeyPress}
                value={request.keyId}
              />
            </Col>
            <Col xs={12} lg={3} className="mt-4">
              <input
                name="aadRevocation"
                type="text"
                placeholder={t('key.aad_revocation')}
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleEnterKeyPress}
                value={request.aadRevocation}
                disabled={request.keyId.length > 0}
              />
            </Col>
            <Col xs={12} lg={3} className="mt-4">
              <button
                onClick={onSearchClick}
                disabled={disableGetKeys}
                className={
                  `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                  'responsive-button btn text-nowrap spacing-right' +
                  (disableGetKeys ? ' btn-outline-primary' : ' btn-primary')
                }
              >
                {t('key.get_key')}
              </button>{' '}
              {showUploadButton == true ? (
                <GenericModal
                  disableModal={disableEditKeys}
                  title={t('key.upload_file')}
                  preOk={validateSaveUploadKeyModal}
                  onOk={handleSaveUploadKeyModal}
                  onClose={handleCloseUploadKeyModal}
                  modalBody={
                    <KeyUpload
                      entityData={formData}
                      setEntityData={setFormData}
                      uploadData={uploadData}
                      setUploadData={setUploadData}
                      setFormReady={setFormReady}
                      ref={refValidateData}
                    />
                  }
                  buttonOkText={t('key.upload')}
                >
                  <button
                    disabled={!showUploadButton || disableUploadKeys}
                    className={
                      `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                      'responsive-button btn text-nowrap' +
                      (!showUploadButton || disableUploadKeys
                        ? ' btn-outline-success'
                        : ' btn-success')
                    }
                  >
                    {t('key.upload_key')}
                  </button>
                </GenericModal>
              ) : null}
            </Col>
          </Row>
        </Container>
        <table className="table table-hover responsive-font mt-3">
          <thead>
            <tr className="border-top">
              <th>{t('key.name')}</th>
              <th>{t('key.key_id')}</th>
              <th>{t('key.aad_revocation')}</th>
              <th>{t('key.last_used')}</th>
              <th>{t('key.created')}</th>
              <th />
              <th />
            </tr>
          </thead>
          <TBody isLoading={loading} TRow={TRow} colSpan="6" />
        </table>
      </div>
      <TFooter
        show={keys.length > 0 && !loading}
        showingText={showingText}
        pageNum={pageNum}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        allowLoadMore={!disableLoadMore}
        onLoadMoreClick={onLoadMoreClick}
      />
    </>
  )
}

export default KeyList
